import React, {useEffect, useRef, useState} from 'react'
import {Box, SvgIconProps, Typography} from '@mui/material'
import moment from 'moment'
import EmailIcon from '@mui/icons-material/Email'
import SmsIcon from '@mui/icons-material/Sms'
import NotificationsIcon from '@mui/icons-material/Notifications'
import LanguageIcon from '@mui/icons-material/Language'
import {ColumnDef} from 'src/components-new/data/DataTable'

export interface CommunicationRow {
  id: string
  sentDate?: string
  title?: string
  message?: string
  plainTextMessage?: string
  type?: string
  hasBeenRead?: boolean
  [key: string]: any
}

interface CommunicationColumnConfig {
  id: string
  label: string
}

// Same value/label pairs as the legacy page (src/pages/communications/Communications.tsx) —
// kept here (duplicated, not imported) so both stacks show identical Italian labels without
// pages-new reaching into src/pages/**.
const NOTIFICATION_TYPES: Record<string, string> = {
  email: 'Messaggio Email',
  sms: 'SMS',
  push: 'Notifica Push',
  web: 'Comunicazione Utente',
}

export function renderNotificationType(type?: string): string {
  if (!type) return '—'
  return NOTIFICATION_TYPES[type] ?? type
}

// Same value/label pairs as the legacy page's DEFAULT_COLUMNS — kept here as the fallback
// so both stacks show identical Italian labels when the PSP hasn't overridden
// `communicationsConfigurations.columnOptions`. The read/unread icon is NOT part of this
// list (see READ_STATUS_COLUMN below) — unlike the legacy table, it's not something a PSP's
// column config can drop, since it's a fixed interaction affordance, not content data.
const DEFAULT_COLUMNS: CommunicationColumnConfig[] = [
  {id: 'sentDate', label: 'DATA INVIO'},
  {id: 'plainTextMessage', label: 'MESSAGGIO'},
  {id: 'type', label: 'TIPO'},
]

// One icon per notification type, so the leftmost column tells you at a glance what kind
// of message a row is, not just whether it's read — email/sms/push/web, same categories as
// NOTIFICATION_TYPES. Falls back to the email icon for an unrecognized/missing type.
const TYPE_ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  email: EmailIcon,
  sms: SmsIcon,
  push: NotificationsIcon,
  web: LanguageIcon,
}

// New-style equivalent of the legacy mdiEmail/mdiEmailOpen icon column, but the icon shape
// now varies by `type` (TYPE_ICONS) instead of always being an envelope — read/unread is
// conveyed by color alone (brand-colored vs. muted), same as before. Used both as the
// desktop column cell and, via `mobileCardBadge`, as the mobile card's top-right indicator.
function renderReadStatus(row: CommunicationRow): React.ReactNode {
  const TypeIcon = (row.type && TYPE_ICONS[row.type]) || EmailIcon
  return <TypeIcon color={row.hasBeenRead ? 'disabled' : 'primary'} fontSize="small" />
}

// The message body can be arbitrarily long (full email text). Clamped to 2 lines, with an
// inline "Leggi tutto"/"Mostra meno" toggle that expands/collapses the text right here in the
// cell — it does NOT open the detail dialog, that's a separate action reached by clicking the
// row itself. The link only appears when the clamp is actually cutting something off; a short
// message that already fits in 2 lines has nothing more to reveal.
function MessageCell({row}: {row: CommunicationRow}): React.ReactElement {
  const textRef = useRef<HTMLElement>(null)
  const [canExpand, setCanExpand] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const text = row.plainTextMessage

  useEffect(() => {
    // Only measure while collapsed — once expanded the clamp is removed, so scrollHeight
    // would equal clientHeight and wrongly report "nothing to expand". Re-checked via
    // ResizeObserver since column width (and thus how much text fits per line) can change.
    if (expanded) return undefined
    const el = textRef.current
    if (!el) return undefined
    const checkTruncation = () => setCanExpand(el.scrollHeight - el.clientHeight > 1)
    checkTruncation()
    const observer = new ResizeObserver(checkTruncation)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, expanded])

  if (!text) return <Typography variant="body2">{'—'}</Typography>

  return (
    <Box>
      <Typography
        ref={textRef}
        sx={expanded ? undefined : {
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        variant="body2"
      >
        {text}
      </Typography>
      {canExpand && (
        <Typography
          onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev) }}
          sx={{color: 'primary.main', cursor: 'pointer', fontWeight: 600, mt: 0.25, '&:hover': {textDecoration: 'underline'}}}
          variant="caption"
        >
          {expanded ? 'Mostra meno' : 'Leggi tutto'}
        </Typography>
      )}
    </Box>
  )
}

// Always the first column, regardless of PSP config — see the comment on DEFAULT_COLUMNS.
const READ_STATUS_COLUMN: ColumnDef<CommunicationRow> = {
  key: 'hasBeenRead',
  header: '',
  width: 40,
  mobileCardBadge: true,
  render: renderReadStatus,
}

function renderCell(id: string) {
  return (row: CommunicationRow): React.ReactNode => {
    if (id === 'sentDate') {
      return (
        <Typography variant="body2">
          {row.sentDate ? moment(row.sentDate).format('DD/MM/YYYY HH:mm') : '—'}
        </Typography>
      )
    }
    if (id === 'plainTextMessage') {
      return <MessageCell row={row} />
    }
    if (id === 'type') {
      return <Typography variant="body2">{renderNotificationType(row.type)}</Typography>
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

/**
 * Columns are PSP-driven (`communicationsConfigurations.columnOptions`), falling back to a
 * sensible default set — mirrors the pattern in materialRequestsColumns.tsx. There is no
 * mobile card header column here (unlike materialRequests' `materialName`) — the list page
 * supplies `mobileCardTitle` reading `row.title` directly, same convention used by
 * VirtualMeetingsListNew for its `title` field. `sentDate` is sortable. READ_STATUS_COLUMN is
 * always prepended regardless of what the PSP config contains (a `hasBeenRead` entry in it,
 * if any — e.g. carried over from the legacy shape — is filtered out to avoid a duplicate).
 * The message column's own "Leggi tutto" toggle (MessageCell) expands/collapses inline and
 * needs no wiring from the caller.
 */
export function buildColumns(columnConfig?: CommunicationColumnConfig[]): ColumnDef<CommunicationRow>[] {
  const config = (columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS)
    .filter(col => col.id !== 'hasBeenRead')
  const contentColumns = config.map((col, index) => ({
    key: `${col.id}-${index}`,
    header: col.label,
    sortKey: col.id === 'sentDate' ? 'sentDate' : undefined,
    render: renderCell(col.id),
  }))
  return [READ_STATUS_COLUMN, ...contentColumns]
}
